import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage, ChatMessageForWS, MessageTypeEnum, ReplyToMessage, } from '../../types/chatMessage';
import { ChatMessageItem } from './ChatMessageItem';
import EmptyPlaceholder from '../_layout/EmptyPlaceholder';
import { useMessagesQuery } from '../../hooks/useChatHistoryQuery';
import { ChatMessageSendForm } from './ChatMessageSendForm';
import { ChatMessageListDayDivider } from './ChatMessageListDayDivider';
import Pencil from '../../assets/pensil.svg';
import { User } from '../../types/users';
import { WebsocketMessageData, WebsocketMessagesTypes } from "../../types/websockets";
import { addUniqueToArray } from "../../helpers/text";
import { debounce } from "lodash";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { useUserContext } from "../../context/user";
import { MessageContextMenu } from "./ContextMenu";
import { MessageRemoveModal } from "./MessageModals";
import { eventEmitter } from "../../helpers/eventEmitter";
import { Media } from '../../types/media';

interface Props {
    chat_pk: string,
    trip_id: string,
    claimPk: number,
    users: User[],
    invalidate: () => void
}

export const ChatMessagesList = ({ chat_pk, trip_id, claimPk, users, invalidate }: Props) => {
    const { userUuid } = useUserContext();

    const [writingUserId, setWritingUserId] = useState<string | null>(null);
    const [writingUser, setWritingUser] = useState<User | null>(null);
    const [messagesList, setMessagesList] = useState<Array<ChatMessage>>([]);
    const [socketUrl, setSocketUrl] = useState(`${process.env.REACT_APP_WEBSOCKET_URL}chat/${chat_pk}/?jwt=${localStorage.getItem('xAuthToken')}`);
    const { sendJsonMessage, lastMessage, readyState } = useWebSocket(socketUrl);

    const {
        data,
        isLoading,
        isSuccess,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch: refetchMessages
    } = useMessagesQuery(trip_id);


    // Отслеживание WS ивентов
    useEffect(() => {
        if (lastMessage !== null) {
            const wsMessage: WebsocketMessageData = JSON.parse(lastMessage.data);
            console.log('wsMessage: ', wsMessage)
            switch (wsMessage?.msg_type) {
                case WebsocketMessagesTypes.NEW_MESSAGE:
                    addNewMessage(wsMessage.data);
                    break;
                case WebsocketMessagesTypes.MESSAGE_UPDATE:
                    updateMessage(wsMessage.data);
                    break;
                case WebsocketMessagesTypes.MESSAGE_READ:
                    findAndSetReadBy(wsMessage.data.message_id, wsMessage.data.reader_id);
                    // счётчик в списке едет коалессированным событием CLAIM_UNREAD_COUNT по ws/chats/ без задержки
                    break;
                case WebsocketMessagesTypes.CHAT_STATUS_UPDATE:
                    refetchMessages().then().catch();
                    invalidate();
                    break;
                case WebsocketMessagesTypes.USER_IS_WRITING:
                    if (!!userUuid && wsMessage.data.sender !== userUuid) { // Печатает другой пользователь
                        setWritingUserId(wsMessage.data.sender);
                        setTimeout(() => setWritingUserId(''), 3000);
                    }
                    break;
                default:
                    break;
            }
        }
    }, [lastMessage])

    const handleClickSendMessage = () => {
        sendJsonMessage({
            // type: WebsocketMessagesTypes.USER_IS_WRITING //TODO backend
            type: 'userIsWriting'
        })
    };

    const connectionStatus = {
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Open',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
    }[readyState];

    const prepareRepliedMessage = (message: ChatMessageForWS) => {
        const foundAuthor = users.find(e => e.pk === message.sender);
        const prepared: ReplyToMessage = {
            pk: message.message_id,
            author: message.sender,
            author_name: foundAuthor ? `${foundAuthor.first_name} ${foundAuthor.last_name}` : 'удалённый участник',
            text: message.text,
            msg_type: message.msg_type,
            important: message.important,
            media: message.media,
        }
        return prepared;
    }


    // Добавление сообщений в рендер по ws ивенту NEW_MESSAGE
    const addNewMessage = (message: ChatMessageForWS) => {
        // console.log(`msg_pk:${message.message_id}|sender_id:${message.sender}`)
        if (message?.msg_type === MessageTypeEnum.TRAVEL_MANAGER_REQUESTED_TRIP_CONFIRMATION) {
            return
        }
        // Добавляем новое сообщение
        setMessagesList(prevState => {
            const newMessage = createChatMessageModel(message)
            let newMessages = prevState.filter(e => e.pk !== newMessage.pk);
            newMessages.push(newMessage);
            // Сортировка по дате
            return newMessages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        })
        // Auto scroll to new msg
        setIsInitialLoad(true);

        // Обновить список вложений
        const media = message?.media
        if (media && media.length > 0) {
            eventEmitter.emit('document', {
                action: 'newMediaMessage',
                media: media,
                messagePk: message.message_id,
                claimPk: claimPk
            });
        }
    };

    // Обновление сообщений в рендер по ws ивенту MESSAGE_UPDATE
    const updateMessage = (message: ChatMessageForWS) => {
        if (message?.msg_type === MessageTypeEnum.TRAVEL_MANAGER_REQUESTED_TRIP_CONFIRMATION) {
            return;
        }

        const chatMessage = createChatMessageModel(message);

        setMessagesList((prevMessages) =>
            prevMessages.map((msg) => (msg.pk === chatMessage.pk ? chatMessage : msg))
        );

        // Обновить модальное окно с превью, если открыто
        const media = message?.media
        if (media && media.length > 0) {
            eventEmitter.emit('document', {
                action: 'documentPreviewIsReady',
                media: media,
                messagePk: message.message_id,
                claimPk: claimPk
            });
        }
    };

    const removeMessage = (messageId: string) => {
        // Удаляем сообщение
        setMessagesList(prevState => {
            let newMessages = prevState.filter(e => e.pk !== messageId);
            // Сортировка по дате
            return newMessages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        })
    };

    // Установка прочитавших по ws ивенту READ_MESSAGE
    const findAndSetReadBy = (msg_pk: string, reader_id: string) => {
        // console.log(`msg_pk:${msg_pk}|reader_id:${reader_id}`)
        // Ищем в состоянии загруженных данных по сообщениям нужное по id и проставляем прочитавших
        setMessagesList(prevState => {
            return prevState.map((item: any) => {
                if (item.pk === msg_pk) {
                    // Добавляем просмотревшего
                    return { ...item, seen_by: addUniqueToArray(item.seen_by, reader_id) };
                }
                return item;
            });
        })
    };

    // Ссылка на контейнер сообщений
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    // Ссылка на нижнюю часть контейнера сообщений
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (data) {
            setMessagesList(prevMessages => {
                // Фильтруем дубликаты
                const uniqueMessages = data.pages.flatMap(page => page.results).filter(newMessage =>
                    !prevMessages.some(existingMessage => existingMessage.pk === newMessage.pk)
                ) || [];

                // Объединяем новые и старые сообщения
                const combinedMessages = [...prevMessages, ...uniqueMessages];
                // Сортируем по дате
                return combinedMessages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

            });
        }
    }, [data]);

    const [isInitialLoad, setIsInitialLoad] = useState(true);
    // Спускаемся в низ списка при первой загрузке сообщений
    useEffect(() => {
        if (isInitialLoad && messagesList.length) {
            // Прокрутка до нижней границы контейнера
            console.log('scroll...', messagesList.length)
            scrollToBottom();
            setIsInitialLoad(false);
        }
    }, [messagesList.length, isInitialLoad]);

    const scrollToBottom = () => {
        // Прокрутка до нижней границы контейнера
        //messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        setPrevScrollHeight(0)
    }

    // Предыдущая высота скролла
    const [prevScrollHeight, setPrevScrollHeight] = useState<number>(0);
    // Высота, на которой срабатывает загрузка истории
    const fetchScrollHeight: number = 500;

    const debouncedFetchNextPage = debounce(() => {
        if (messagesContainerRef.current) {
            const { scrollTop, clientHeight, scrollHeight } = messagesContainerRef.current;
            // console.log(`-----------SCROLL------------`);
            // console.log(`fetchScrollHeight:${fetchScrollHeight}`);
            // console.log(`SCROLL-clientHeight:${clientHeight}--scrollHeight:${scrollHeight}--scrollTop:${scrollTop}-`);
            // console.log(`------------------------------`);

            // Если есть новая страница, нет текущей загрузки, высота до верха меньше высоты срабатывания
            if (scrollTop < fetchScrollHeight && hasNextPage && !isFetchingNextPage) {
                setPrevScrollHeight(scrollHeight); // Устанавливаем текущую высоту контейнера скролла
                fetchNextPage().then().catch()
            }
        }
    }, 200)

    // Отслеживание скролла
    useEffect(() => {
        const handleScroll = () => {
            debouncedFetchNextPage();
        };

        if (messagesContainerRef.current) {
            messagesContainerRef.current.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (messagesContainerRef.current) {
                messagesContainerRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    // Отслеживание изменений предыдущей высоты скролла для сохранения положения при подгрузке новых сообщений
    useEffect(() => {
        if (messagesContainerRef.current) {
            const { scrollHeight } = messagesContainerRef.current;
            if (prevScrollHeight > 0) {
                const newScrollTop = scrollHeight - prevScrollHeight;
                // После загрузки предыдущих сообщений и изменении prevScrollHeight
                // сохраняем положение скролла на том же месте
                // Устанавливаем, только если новое положение больше нуля
                // console.log(`SCROLL-newScrollTop:${newScrollTop}`);
                // console.log(`SCROLL-prevScrollHeight:${prevScrollHeight}`);
                // console.log(`------------------------------`);

                if (newScrollTop > 0) {
                    messagesContainerRef.current.scrollTop = newScrollTop;
                }
            }
        }
        // Отслеживаем кол-во сообщений и предыдущую высоту
    }, [messagesList.length, prevScrollHeight]);

    // Модальное окно удаления сообщения открыто
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const openRemoveModal = () => {
        setIsRemoveModalOpen(true)
    };
    // Сообщение для ответа
    const [messageToReply, setMessageToReply] = useState<ReplyToMessage | null>(null);
    // Сообщение для контекстного меню
    const [messageForContextMenu, setMessageForContextMenu] = useState<string | null>(null);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    // Ссылка на родительский контейнер
    const parentRef = useRef<HTMLDivElement>(null);
    const handleContextMenu = (messageId: string) => (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        if (parentRef.current) {
            const parentRect = parentRef.current.getBoundingClientRect();

            const x = event.clientX - parentRect.left;
            const y = event.clientY - parentRect.top;
            setContextMenuPosition({ x, y });
        }
        setMessageForContextMenu(messageId);
    };

    // Сообщение с разделителем
    const renderDividedMessage = (chatMessage: ChatMessage, i: number, array: ChatMessage[]) => {
        const dt = new Date(chatMessage.created_at)
        const prevDate = array[i - 1]?.created_at && new Date(array[i - 1]?.created_at).getDate();
        // Контекстное меню только для НЕ системных сообщений
        const isAllowDisplayContextMenu = chatMessage.author != null
        const messageBlockParentClasses = 'w-full flex-[0_0_auto] overflow-x-hidden'
        const denyHandleClick = (event: React.MouseEvent) => event.preventDefault();
        const messageBlock = () => (
            <>
                {/* DIVIDER */}
                {(i === 0 || dt.getDate() !== prevDate) && <ChatMessageListDayDivider dt={dt} />}
                {/* Message */}
                <ChatMessageItem
                    key={chatMessage.pk}
                    msg={chatMessage}
                    sender={users.find(obj => obj.pk === chatMessage.author)}
                    getFile={(id) => {
                        const media = messagesList.find(msg => msg.pk === chatMessage.pk)?.media.find(media => media.id === id)
                        return media
                    }}
                />
            </>
        );
        // Единый обработчик событий который вызывает меню в зависимости от условий или optional props не сработали, поэтому так
        if (isAllowDisplayContextMenu) {
            return <div
                className={messageBlockParentClasses}
                onContextMenu={handleContextMenu(chatMessage.pk)}

                key={chatMessage.pk}
            >
                {messageBlock()}
            </div>
        }

        return <div
            className={messageBlockParentClasses}
            onContextMenu={denyHandleClick}
            key={chatMessage.pk}
        >
            {messageBlock()}
        </div>
    }

    const handleCloseContextMenu = () => {
        setMessageForContextMenu(null)
    };

    const handleCloseRemoveModal = () => {
        setMessageForContextMenu(null);
        setIsRemoveModalOpen(false)
    };

    const handleOnDeleteMessage = (messageId: string) => {
        removeMessage(messageId);
        setMessageForContextMenu(null);
        setIsRemoveModalOpen(false)
    };

    const handleReply = () => {
        const found = messagesList.find(e => e.pk === messageForContextMenu)

        if (found && found.author) {
            const foundAuthor = users.find(e => e.pk === found.author);
            const prepared: ReplyToMessage = {
                pk: found.pk,
                author: found.author,
                author_name: foundAuthor ? `${foundAuthor.first_name} ${foundAuthor.last_name}` : 'удалённый участник',
                text: found.text,
                msg_type: found.msg_type,
                important: found.important,
                media: found.media,
            }
            setMessageToReply(prepared) // Сообщение для ответа берём из сообщения в контекстном меню
            setMessageForContextMenu(null); // Сообщение в контекстном меню очищаем
        }
    }
    const handleCancelReply = () => {
        setMessageToReply(null) // Сообщение для ответа очищаем
    }

    const handleMessageSent = () => {
        scrollToBottom();
        setMessageToReply(null);
    }

    // Себеседние печатает, определение пользователя из списка участников чата
    useEffect(() => {
        const user = users.find(obj => obj.pk === writingUserId);
        if (user) {
            setWritingUser(user)
        }
    }, [writingUserId]);

    // Отображение пункта удаления сообщения только для своих сообщений
    const isAllowDeleteOptionForMessage = (): boolean => {
        const found = messagesList.find(e => e.pk === messageForContextMenu);
        const res = found && userUuid && found.author === userUuid;
        return res || false;
    };

    const createChatMessageModel = (socketMessage: ChatMessageForWS) => {
        const chatMessage: ChatMessage = {
            pk: socketMessage.message_id,
            seen_by: [],
            media: socketMessage.media,
            chat: chat_pk,
            created_at: socketMessage.created_at,
            author: socketMessage.sender,
            text: socketMessage.text,
            important: socketMessage.important,
            msg_type: socketMessage.msg_type,
            replied_to: socketMessage.replied_to ? prepareRepliedMessage(socketMessage.replied_to) : null
        }

        return chatMessage
    }

    return (
        <div
            ref={parentRef}
            className="flex w-full flex-col justify-between overflow-hidden relative"
        >
            <MessageContextMenu
                openRemoveModal={openRemoveModal}
                x={contextMenuPosition.x}
                y={contextMenuPosition.y}
                show={!!messageForContextMenu}
                isAllowDeleteOption={isAllowDeleteOptionForMessage()}
                onClose={handleCloseContextMenu}
                onReply={handleReply}
            />

            <MessageRemoveModal
                isOpen={isRemoveModalOpen}
                messageId={messageForContextMenu}
                onClose={handleCloseRemoveModal}
                onDelete={handleOnDeleteMessage}
            />

            {isLoading && <div className='mb-1 flex flex-row justify-center w-full italic text-melon-grey'>
                загрузка истории...
            </div>}

            {/*Messages Container*/}
            <div
                className='relative w-full overflow-hidden flex flex-col justify-between'
            >
                {/*Заглушка для пустоты*/}
                {!messagesList.length && !isLoading && <EmptyPlaceholder text={'Здесь пока пусто'} />}
                {/*Список сообщений*/}
                <div
                    ref={messagesContainerRef}
                    className='flex flex-col w-full p-5 overflow-y-auto overflow-x-hidden relative'
                >
                    {isFetchingNextPage &&
                        <div className='mb-1 flex flex-row justify-center w-full italic text-melon-grey'>
                            загрузка истории...
                        </div>
                    }
                    {!!messagesList?.length && messagesList.map((e: ChatMessage, i: number, array: ChatMessage[]) => renderDividedMessage(e, i, array))}
                    <div ref={messagesEndRef}></div>
                </div>
                {/*Индикатор - собеседник печатает*/}
                {writingUserId &&
                    <div className='text-melon-grey mb-1 ml-6 absolute left-0 bottom-0'>
                        <img
                            className='inline mr-3'
                            src={Pencil}
                            style={{ height: 16, width: 16 }}
                            alt="pensil"
                        />
                        {writingUser ? `${writingUser.first_name} ${writingUser.last_name}` : 'Кто-то не из чата '} печатает...
                    </div>
                }
            </div>

            {/*Форма отправки сообщений*/}
            <div className='flex flex-col rounded-b-lg px-5 shadow-above'>
                <ChatMessageSendForm
                    messagesFetching={isFetching}
                    chatId={chat_pk}
                    onMessageSend={handleMessageSent}
                    onTyping={handleClickSendMessage}
                    messageToReply={messageToReply}
                    onCancelReply={handleCancelReply}
                />
            </div>
        </div>
    );
};
